/**
 * Inline `DocChunk#N` reference rendered inside an evidence fact.
 *
 * Rendered as plain inline text (blue, unstyled — not a badge). When the
 * reference's chunk resolves to a known page in the clinical notes it stays
 * clickable to deep-link the left-hand PDF to that page; otherwise it is a
 * static, non-interactive label so the reference stays visible without a target.
 * @module DocChunkChip
 */
import React from "react";
import { ChunkLocation } from "../utils/evidenceText";

/** Props for {@link DocChunkChip}. */
interface DocChunkChipProps {
  chunkNumber: number;
  location?: ChunkLocation;
  onNavigate?: (page: number) => void;
}

/**
 * Shared styling for the reference, interactive or not: plain blue inline text
 * (Tertiary-Blue-3, 11px Lato) that reads as part of the surrounding prose.
 */
const REFERENCE_CLASS =
  "font-['Lato'] text-[0.6875rem] leading-[1.03125rem] font-normal " +
  "text-[#0041A3]";

/**
 * Renders a single `DocChunk#N` reference as plain inline text.
 *
 * @param props - Chunk number, its resolved location, and the navigate handler
 * @returns A clickable reference when it has a page, else a static label
 */
export const DocChunkChip: React.FC<DocChunkChipProps> = ({
  chunkNumber,
  location,
  onNavigate,
}) => {
  const page = location?.page;
  const label = `DocChunk#${chunkNumber}`;

  if (page && onNavigate) {
    return (
      <button
        type="button"
        onClick={() => onNavigate(page)}
        aria-label={`${label} — go to page ${page}`}
        className={`${REFERENCE_CLASS} cursor-pointer border-0 bg-transparent p-0 hover:underline focus:outline-none focus-visible:underline`}
      >
        {label}
      </button>
    );
  }

  return <span className={REFERENCE_CLASS}>{label}</span>;
};
